import { clsx } from 'clsx';
import { useMemo, useState } from 'react';
import ShopLink from '~/components/Links/ShopLink';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import { RailsFacet, RailsFacetGroup } from '~/typings/services/rails/facets';

const VISIBLE_LIMIT = 5;

export default function LinkedFiltersList() {
  const { facets, lander } = useFacetFiltering();

  // Take the top 50 facets, regardless of section, sorted by facet count, re-grouped by facet group
  const topFacets = useMemo(
    () =>
      facets
        .flatMap<RailsFacet & { facetGroup: RailsFacetGroup }>(
          facetGroup =>
            // Add facetGroup to each child so we can group them later
            facetGroup.children?.map(facet => ({
              ...facet,
              facetGroup,
            })) || [],
        )
        .filter(
          facet =>
            !facet.url?.endsWith(lander?.path || '') && // Exclude the current lander
            facet.count! > 0 &&
            facet.destination_lander_name,
        )
        .sort((a, b) => b.count! - a.count!)
        .slice(0, 50)

        // Convert to a record of links grouped by facetGroup, delete children
        .reduce<
          Record<
            string,
            Omit<RailsFacetGroup, 'children'> & { links: RailsFacet[] }
          >
        >((acc, facet) => {
          const key = facet.facetGroup.slug;
          acc[key] = {
            links: (acc[key]?.links || []).concat(facet),
            children: undefined,
            ...facet.facetGroup,
          };
          return acc;
        }, {}),
    [facets, lander?.path],
  );

  const values = Object.values(topFacets);
  if (values.length == 0) {
    return null;
  }

  return (
    <section className="bg-grey-100 mt-8 py-8" id="linked-filters-section">
      <div className="container">
        <div className="mb-4 flex items-baseline gap-4">
          <h3 className="inline-flex text-lg">Related Searches</h3>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {values.map(facetGroup => (
            <LinkedFiltersColumn
              key={facetGroup.slug}
              facetGroup={facetGroup}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function LinkedFiltersColumn({
  facetGroup,
}: {
  facetGroup: Omit<RailsFacetGroup, 'children'> & { links: RailsFacet[] };
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <h4 className="mb-2 font-semibold">{facetGroup.name}</h4>
      <div className="divide-y">
        {facetGroup.links?.map((facet, i) => (
          <ShopLink
            key={facet.slug}
            path={facet.url}
            className={clsx(
              'block py-1',
              !expanded && i > VISIBLE_LIMIT - 1 && 'hidden',
            )}
          >
            {facet.destination_lander_name}
          </ShopLink>
        ))}

        {!expanded && facetGroup.links?.length! > VISIBLE_LIMIT - 1 && (
          <div>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="py-1 pr-4 font-semibold text-green-500"
            >
              Show more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
